import { useMemo } from 'react';
import Fuse, { IFuseOptions } from 'fuse.js';
import { RESTAURANT_SEARCH_SYNONYMS } from './resources/synonyms';

export interface UseFuzzySearchOptions<T> {
  /**
   * The keys/fields to search within the objects
   */
  keys: string[];
  /**
   * The search query string
   */
  query: string;
  /**
   * The data array to search through
   */
  data: T[];
  /**
   * Fuse.js threshold (0.0 = perfect match, 1.0 = match anything)
   * Default: 0.4 (good balance for typos and fuzzy matching)
   */
  threshold?: number;
  /**
   * The distance to determine how far the match must be from the pattern
   * Default: 100
   */
  distance?: number;
  /**
   * Minimum character length of the query before searching
   * Default: 0 (search on any input)
   */
  minMatchCharLength?: number;
  /**
   * Enable synonym matching when initial results are below this threshold
   * Default: 5
   */
  synonymThreshold?: number;
  /**
   * Enable synonym search
   * Default: true
   */
  enableSynonyms?: boolean;
}

/**
 * Get synonyms for a search query
 * @param query - The search query
 * @returns Array of synonym terms to also search for
 */
function getSynonyms(query: string): string[] {
  const normalizedQuery = query.toLowerCase().trim();
  const synonyms = RESTAURANT_SEARCH_SYNONYMS[normalizedQuery];

  if (!synonyms || synonyms.length === 0) {
    return [];
  }

  return synonyms;
}

/**
 * Custom hook for fuzzy searching using Fuse.js
 * Provides intelligent matching for typos, partial matches, and similar terms
 * Includes synonym matching for cross-language support (e.g., "salmon" ↔ "zalm")
 *
 * @example
 * const filteredProducts = useFuzzySearch({
 *   data: products,
 *   keys: ['name', 'description'],
 *   query: searchQuery,
 *   threshold: 0.4
 * });
 */
export function useFuzzySearch<T>({
  data,
  keys,
  query,
  threshold = 0.7,
  distance = 100,
  minMatchCharLength = 1,
  synonymThreshold = 5,
  enableSynonyms = true,
}: UseFuzzySearchOptions<T>): T[] {
  // Callers pass `keys` as an inline array literal (new reference every render),
  // which would invalidate the memo below and rebuild the whole Fuse index on
  // every keystroke. Depend on the joined string instead of the array identity.
  const keysSignature = keys.join('|');

  const fuse = useMemo(() => {
    const options: IFuseOptions<T> = {
      keys,
      threshold,
      distance,
      minMatchCharLength,
      // Include score and matches for potential debugging
      includeScore: false,
      includeMatches: false,
      // Use extended search for more flexibility
      useExtendedSearch: false,
      // Ignore location for better cross-document matching
      ignoreLocation: true,
      // Case insensitive by default
      isCaseSensitive: false,
      // Find all matches, not just best match
      findAllMatches: true,
    };

    return new Fuse(data, options);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keysSignature stands in for `keys`
  }, [data, keysSignature, threshold, distance, minMatchCharLength]);

  const results = useMemo(() => {
    // If no query, return all data
    if (!query || !query.trim()) {
      return data;
    }

    const trimmedQuery = query.trim();

    // Perform initial fuzzy search
    const initialResults = fuse.search(trimmedQuery);
    const initialItems = initialResults.map(result => result.item);

    // If we have enough results or synonyms are disabled, return initial results
    if (!enableSynonyms || initialItems.length >= synonymThreshold) {
      return initialItems;
    }

    // Try synonym search
    const synonyms = getSynonyms(trimmedQuery);

    if (synonyms.length === 0) {
      return initialItems;
    }

    // Search for each synonym and collect results
    const synonymResults = new Set<T>(initialItems);

    for (const synonym of synonyms) {
      const synonymSearchResults = fuse.search(synonym);
      synonymSearchResults.forEach(result => synonymResults.add(result.item));
    }

    return Array.from(synonymResults);
  }, [fuse, query, data, enableSynonyms, synonymThreshold]);

  return results;
}
